import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns';
import isString from 'lodash-es/isString';
import { MutationCache, QueryCache, QueryClient, } from '@tanstack/react-query';
import { notifyHB } from './Observability';
export var StaleTime;
(function (StaleTime) {
    StaleTime[StaleTime["NEVER"] = 0] = "NEVER";
    StaleTime[StaleTime["SHORT"] = minutesToMilliseconds(5)] = "SHORT";
    StaleTime[StaleTime["LONG"] = hoursToMilliseconds(1)] = "LONG";
    StaleTime[StaleTime["FOREVER"] = Infinity] = "FOREVER";
})(StaleTime || (StaleTime = {}));
const createErrorGroupIdentifier = (key) => key?.find(isString);
/**
 * Creates a new `QueryClient` instance with error handling and any additional configuration.
 *
 * @example
 * import { Query } from '@catawiki/runtime-toolkit';
 *
 * const queryClient = Query.createClient();
 *
 * <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
 */
export const createClient = ({ defaultOptions, queryCacheOptions, mutationCacheOptions, } = {}) => new QueryClient({
    defaultOptions,
    queryCache: new QueryCache({
        ...queryCacheOptions,
        onError(error, query) {
            queryCacheOptions?.onError?.(error, query);
            notifyHB(error, {
                fingerprint: createErrorGroupIdentifier(query.options.queryKey),
            });
        },
    }),
    mutationCache: new MutationCache({
        ...mutationCacheOptions,
        onError(error, variables, context, mutation) {
            mutationCacheOptions?.onError?.(error, variables, context, mutation);
            notifyHB(error, {
                fingerprint: createErrorGroupIdentifier(mutation.options.mutationKey),
            });
        },
    }),
});
export const createTestClient = () => createClient({
    defaultOptions: { queries: { retry: false, staleTime: Infinity } },
});
/**
 * Extract a value from the given query by pattern matching on the different states.
 * This is ideal for when rendering JSX based on different states of a query.
 *
 * If you need to match multiple queries then consider using `useQueries` to group them.
 * The return value of `useQueries` can then be passed to this function.
 *
 * @example
 * import { pipe } from 'fp-ts/function';
 * import { useQuery } from '@tanstack/react-query';
 *
 * import { Query } from '@catawiki/runtime-toolkit';
 *
 * const query = useQuery(...);
 *
 * return (
 *   <div>
 *     {pipe(
 *       query,
 *       Query.match({
 *         idle: () => null,
 *         pending: () => <Spinner />,
 *         failure: ({ refetch }) => (
 *           <Banner primaryAction={<Button onClick={refetch}>Retry</Button>}>
 *            Something went wrong.
 *           </Banner>
 *         ),
 *         success: ({ data }) => (
 *          <article>
 *            <h1>{data.title}</h1>
 *            <p>{data.description}</p>
 *            <Button>Got it!</Button>
 *          </article>
 *         )
 *       })
 *     )}
 *   </div>
 * );
 */
export const match = (matcher) => (query) => {
    if (query.isSuccess) {
        return matcher.success({
            data: query.data,
            dataUpdatedAt: query.dataUpdatedAt,
            isPlaceholderData: query.isPlaceholderData,
            refetch: query.refetch,
        });
    }
    if (query.isError) {
        return matcher.failure({
            error: query.error,
            errorUpdatedAt: query.errorUpdatedAt,
            errorUpdateCount: query.errorUpdateCount,
            failureCount: query.failureCount,
            failureReason: query.failureReason,
            isLoadingError: query.isLoadingError,
            isRefetchError: query.isRefetchError,
            refetch: query.refetch,
        });
    }
    if (query.isFetching || query.isLoading || query.isRefetching) {
        return matcher.fetching({
            data: query.data,
            isLoading: query.isLoading,
            isPlaceholderData: query.isPlaceholderData,
            isRefetching: query.isRefetching,
        });
    }
    return matcher.idle({
        isPaused: query.isPaused,
        isPending: query.isPending,
        isStale: query.isStale,
    });
};
/**
 * Extract a value from the given mutation by pattern matching on the different states.
 * This is ideal for when rendering JSX based on different states of a mutation.
 *
 * @example
 * import { pipe } from 'fp-ts/function';
 * import { useMutation } from '@tanstack/react-query';
 *
 * import { Query } from '@catawiki/runtime-toolkit';
 *
 * const mutation = useMutation(...);
 *
 * return (
 *   <div>
 *     {pipe(
 *       mutation,
 *       Query.matchMutation({
 *         idle: () => null,
 *         pending: () => <Spinner />,
 *         failure: ({ variables }) => (
 *           <Banner
 *             primaryAction={
 *               <Button onClick={() => mutation.mutate(variables)}>
 *                 Retry
 *               </Button>
 *             }
 *           >
 *            Something went wrong.
 *           </Banner>
 *         ),
 *         success: ({ data }) => (
 *          <article>
 *            <h1>{data.title}</h1>
 *            <p>{data.description}</p>
 *            <Button>Got it!</Button>
 *          </article>
 *         )
 *       })
 *     )}
 *   </div>
 * );
 */
export const matchMutation = (matcher) => (mutation) => {
    if (mutation.isSuccess) {
        return matcher.success({
            data: mutation.data,
            reset: mutation.reset,
            submittedAt: mutation.submittedAt,
            variables: mutation.variables,
        });
    }
    if (mutation.isError) {
        return matcher.failure({
            error: mutation.error,
            failureCount: mutation.failureCount,
            failureReason: mutation.failureReason,
            reset: mutation.reset,
            submittedAt: mutation.submittedAt,
            variables: mutation.variables,
        });
    }
    if (mutation.isPending) {
        return matcher.pending({
            data: mutation.data,
            submittedAt: mutation.submittedAt,
            variables: mutation.variables,
        });
    }
    return matcher.idle();
};
